import React, { useRef } from 'react'
import { toast } from 'react-toastify'

import { EditorState, convertToRaw } from 'draft-js'

import {
    AttachFile as AttachFileIcon,
    Send as SendIcon,
} from '@mui/icons-material'
import { useTheme, IconButton } from '@mui/material'

import { RichTextEditor } from '../RichTextEditor'
import { compositeDecorator } from './decorators'
import { AxiosResponse } from 'axios'
import { Company } from '../../../types/company'
import { CurrentChannel, MessageDetail } from '../../../types/message'
import MentionOption from '../RichTextEditor/ContextMenu/MentionOption'
import { AttachmentContainer } from '../AttachmentContainer'
import { FlexDiv } from '../../../components'

interface Props {
    editorState: EditorState
    currentChannel?: CurrentChannel | null
    fileList?: File[]
    disableMessaging?: boolean
    setFileList: (fileList?: File[]) => void
    setEditorState: (editorState: EditorState) => void
    createMessage: (formData: FormData) => Promise<AxiosResponse<MessageDetail>>
    onCreateMessage?: (messageDetail: MessageDetail) => void
}

export const RichTextInput = (props: Props) => {
    const {
        editorState,
        setEditorState,
        currentChannel,
        fileList,
        setFileList,
        disableMessaging,
    } = props

    const _createMessage = props.createMessage

    const attachmentRef = useRef<HTMLInputElement>(null)

    const theme = useTheme()

    const onChangeEditorState = (editorState: EditorState) => {
        setEditorState(editorState)
    }

    const mentionOptions: { id: number; name: string }[] = []

    currentChannel?.members.forEach((mem) => {
        if (mem.member_type === 'user') {
            if (
                mentionOptions.find((opt) => opt.id === mem.member.id) ===
                undefined
            ) {
                mentionOptions.push({
                    id: mem.member.id,
                    name: mem.member.name,
                })
            }
        } else {
            const comp = mem.member as Company
            comp.employees?.forEach((user) => {
                if (
                    mentionOptions.find((opt) => opt.id === user.id) ===
                    undefined
                ) {
                    mentionOptions.push({
                        id: user.id,
                        name: user.name,
                    })
                }
            })
        }
    })

    return (
        <FlexDiv
            style={{
                flex: 1,
                flexDirection: 'column',
                padding: theme.spacing(0, 1, 1, 1),
            }}
        >
            <AttachmentContainer
                ref={attachmentRef}
                fileList={fileList}
                setFileList={setFileList}
                theme={theme}
            />

            <RichTextEditor
                editorState={editorState}
                onChange={onChangeEditorState}
                right={() => {
                    return (
                        <FlexDiv style={{ alignItems: 'center' }}>
                            <div
                                onClick={() =>
                                    attachmentRef.current &&
                                    attachmentRef.current.click()
                                }
                                style={{ cursor: 'pointer' }}
                            >
                                <AttachFileIcon fontSize="large" />
                            </div>
                            <IconButton
                                color="primary"
                                onClick={() => {
                                    let canMakePost = false
                                    const message = new FormData()
                                    if (
                                        currentChannel === undefined ||
                                        currentChannel === null
                                    ) {
                                        return
                                    } else {
                                        message.append(
                                            'channel',
                                            String(currentChannel.id),
                                        )
                                    }

                                    if (
                                        editorState
                                            .getCurrentContent()
                                            .getPlainText().length > 0
                                    ) {
                                        message.append(
                                            'content',
                                            editorState
                                                .getCurrentContent()
                                                .getPlainText(),
                                        )
                                        canMakePost = true
                                    }
                                    if (fileList && fileList.length > 0) {
                                        canMakePost = true
                                        message.append(
                                            'attachments_len',
                                            String(fileList.length),
                                        )
                                        for (
                                            let i = 0;
                                            i <= fileList.length;
                                            i++
                                        ) {
                                            const attachmentNum = `attachment${i}`
                                            message.append(
                                                attachmentNum,
                                                fileList[i],
                                            )
                                        }
                                    }
                                    //now handle mentions
                                    const entityMap = convertToRaw(
                                        editorState.getCurrentContent(),
                                    ).entityMap
                                    let tags_user_len = 0

                                    for (const key in entityMap) {
                                        message.append(
                                            `tag_user${tags_user_len}`,
                                            entityMap[key].data.mention.id,
                                        )
                                        tags_user_len++
                                    }
                                    if (tags_user_len > 0) {
                                        message.append(
                                            `tags_user_len`,
                                            String(tags_user_len),
                                        )
                                    }

                                    if (canMakePost) {
                                        console.log('Sending message')
                                        console.log(message)
                                        //send the message
                                        _createMessage(message).then((res) => {
                                            props.onCreateMessage &&
                                                props.onCreateMessage(res.data)
                                        })
                                        onChangeEditorState(
                                            EditorState.createEmpty(
                                                compositeDecorator,
                                            ),
                                        )
                                        setFileList(undefined)
                                    } else {
                                        toast.error('No Message Content')
                                    }
                                }}
                                size="large"
                                disabled={disableMessaging}
                            >
                                <SendIcon />
                            </IconButton>
                        </FlexDiv>
                    )
                }}
                customStyle={{
                    editor: {
                        padding: theme.spacing(1.5, 0.5),
                    },
                }}
                mentionOptions={mentionOptions}
                renderMentionOption={(opt, idx) => {
                    return (
                        <MentionOption
                            key={`MENTION_OPT_${idx}`}
                            option={opt}
                        />
                    )
                }}
            />
        </FlexDiv>
    )
}
